import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { withTranslation } from "../translation/";
import {
  getFacets,
  getStateForPriceSlider,
  done,
  changeMultiFacetAndApplyIfLargeMedium,
  selectAll,
  clearAll,
  clear,
  clearSelections
} from "../../state/modules/search";
import { COLOUR_FACET_ID } from "../../state/modules/facet/constants";
import { getSwatches } from "../../state/modules/facet/selectors";
import { analyticsOpenRefinementType } from "../../state/modules/analytics/actions";
import {
  getFilterOverlayVisible,
  getSecondaryListIsOpen
} from "../../state/modules/layout/selectors";
import {
  hideFilterOverlay,
  toggleSecondaryList
} from "../../state/modules/layout/actions";
import { getColourEnrichments } from "../../state/modules/facet/actions";
import styles from "./RefinementList.css";
import PrimaryList from "./PrimaryList";
import SecondaryList from "./SecondaryList";
import classNames from "classnames/bind";
import { getFacetWithSwatches } from "../../client/getFacetWithSwatches";
import { MOBILE_VIEW_ITEMS } from "../../state/modules/search/constants";
import commonConfig from "../../config/common";

const styleContext = classNames.bind(styles);
const { newFacets } = commonConfig;

export const RefinementList = ({ formatTranslation, formatCurrency }) => {
  const dispatch = useDispatch();
  const [openItemId, setOpenItemId] = useState(null);

  const secondaryListIsOpen = useSelector(getSecondaryListIsOpen);
  const range = useSelector(getStateForPriceSlider);
  const facets = useSelector(getFacets);
  const itemCount = useSelector(state => state.search.itemCount);
  const isVisible = useSelector(getFilterOverlayVisible);
  const swatchesMap = useSelector(getSwatches);
  const colourFilterPresent = useSelector(state =>
    getFacets(state).some(({ id }) => id === COLOUR_FACET_ID)
  );

  const handleToggleSecondaryList = () => dispatch(toggleSecondaryList());
  const handleHideFilterOverlay = () => dispatch(hideFilterOverlay());
  const handleChangeMultiFacet = (...args) =>
    dispatch(changeMultiFacetAndApplyIfLargeMedium(...args));
  const handleDone = (...args) => dispatch(done(...args));
  const handleSelectAll = (...args) => dispatch(selectAll(...args));
  const handleClearAll = (...args) => dispatch(clearAll(...args));
  const handleClear = (...args) => dispatch(clear(...args));
  const handleClearSelections = (...args) => dispatch(clearSelections(...args));
  const handleAnalyticsOpenRefinementType = (...args) =>
    dispatch(analyticsOpenRefinementType(...args));
  const handleGetColourEnrichments = () => dispatch(getColourEnrichments());

  useEffect(() => {
    if (isVisible) {
      if (secondaryListIsOpen) {
        handleToggleSecondaryList();
      }
      handleClearSelections();
    }
  }, [isVisible]);

  useEffect(() => {
    if (colourFilterPresent) {
      handleGetColourEnrichments();
    }
  }, [colourFilterPresent]);

  const getOpenItem = () => {
    const facet = facets.find(({ id }) => id === openItemId);

    if (!facet || facet.id !== COLOUR_FACET_ID) {
      return facet;
    }

    return getFacetWithSwatches({ facet, swatchesMap });
  };

  const setItemCount = (facetId = false) => {
    if (facetId) {
      const selectedFacet = facets.filter(
        f => f.id === facetId && f.hasSelectedValues
      );

      if (selectedFacet.length) {
        return selectedFacet[0].facetValues
          .map(fv => {
            if (fv.isSelected === true) {
              return fv.count;
            }

            return 0;
          })
          .reduce((accumulator, count) => accumulator + count);
      }
    }

    return itemCount;
  };

  const open = ({ id, name }) => {
    setOpenItemId(id);
    if (!secondaryListIsOpen) {
      handleToggleSecondaryList();
    }
    handleAnalyticsOpenRefinementType({ id, name });
  };

  const back = () => {
    if (secondaryListIsOpen) {
      handleToggleSecondaryList();
    }
  };

  const handleViewListClick = facet => {
    handleDone(facet, MOBILE_VIEW_ITEMS);
    handleHideFilterOverlay();
    if (secondaryListIsOpen) {
      handleToggleSecondaryList();
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className={styles.refinementList}>
      <div
        className={styleContext({
          navigation: true,
          navigation__secondary: secondaryListIsOpen
        })}
      >
        <PrimaryList
          facets={facets}
          newFacets={newFacets}
          range={range}
          clearAll={handleClearAll}
          formatTranslation={formatTranslation}
          formatCurrency={formatCurrency}
          itemCount={itemCount}
          open={open}
          viewList={handleViewListClick}
          styles={styles}
        />
        <SecondaryList
          facet={getOpenItem()}
          itemCount={getOpenItem() ? setItemCount(openItemId) : 0}
          styles={styles}
          clear={handleClear}
          selectAll={handleSelectAll}
          changeMultiFacet={handleChangeMultiFacet}
          viewList={handleViewListClick}
          back={back}
          secondaryListIsOpen={secondaryListIsOpen}
          formatTranslation={formatTranslation}
        />
      </div>
    </div>
  );
};

RefinementList.propTypes = {
  formatTranslation: PropTypes.func.isRequired,
  formatCurrency: PropTypes.func.isRequired
};

RefinementList.displayName = "RefinementList";

export default withTranslation(RefinementList);
